import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, Lock, Loader2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { publicTenantMeta, verifyTenant } from "../lib/adminApi";

/**
 * TenantGate — müşteri demo şifre kapısı.
 * /demo/:slug — şifre doğru → tenant bilgileri sessionStorage'a kaydedilip /personas'a yönlendirilir.
 */
export default function TenantGate() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    publicTenantMeta(slug)
      .then((d) => setMeta(d))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const data = await verifyTenant(slug, password);
      sessionStorage.setItem("plena_tenant", JSON.stringify(data.tenant));
      nav("/personas", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Şifre doğrulanamadı");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-7 h-7 text-rose-600" />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Demo bulunamadı</h2>
          <p className="text-sm text-slate-500 mt-2">
            <code>/demo/{slug}</code> linki geçersiz veya kaldırılmış. Lütfen Plena Growth ekibi ile iletişime geçin.
          </p>
        </div>
      </div>
    );
  }

  const brand = meta.brand_color || "#0EA5E9";

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: `linear-gradient(135deg, ${brand}15, #f8fafc 50%)` }}>
      <div className="w-full max-w-sm">
        {/* Tenant brand */}
        <div className="flex items-center gap-3 justify-center mb-8">
          {meta.logo_url ? (
            <div className="flex items-center gap-3">
              <img src={meta.logo_url} alt={meta.name} className="h-12 max-w-[180px] object-contain" />
              <div className="text-[0.6rem] uppercase tracking-wider text-slate-500 border-l border-slate-200 pl-3 leading-tight">
                Plena Growth<br /><span className="text-slate-400">Demo</span>
              </div>
            </div>
          ) : (
            <>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base shadow-md" style={{ background: brand }}>
                {meta.name?.charAt(0)}
              </div>
              <div className="leading-tight text-left">
                <div className="font-display text-lg font-bold tracking-tight text-slate-900">{meta.name}</div>
                <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">Plena Growth Demo</div>
              </div>
            </>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-7">
          <div className="mb-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: `${brand}20`, color: brand }}>
              <Lock className="w-5 h-5" />
            </div>
            <h2 className="font-display text-xl font-semibold text-slate-900">Demo Erişimi</h2>
            <p className="text-xs text-slate-500 mt-1">
              Sizinle paylaşılan şifreyi girerek devam edin.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pwd" className="text-xs font-semibold text-slate-700">Erişim Şifresi</Label>
              <Input
                id="pwd"
                data-testid="tenant-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
                placeholder="••••••••••"
                required
                autoFocus
              />
            </div>
            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="tenant-gate-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="tenant-gate-submit"
              disabled={submitting}
              className="w-full h-11 text-white font-semibold gap-2"
              style={{ background: brand }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {submitting ? "Doğrulanıyor…" : "Demoya Giriş Yap"}
            </Button>
          </form>
        </div>

        <p className="text-center text-[0.65rem] text-slate-400 mt-6">
          © 2026 Plena Labs · Plena Growth Talent Intelligence
        </p>
      </div>
    </div>
  );
}
